import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const EMPTY_CLASSIFICATIONS = {
  defaultAgencies:   [], customAgencies:   [],
  defaultRoadTypes:  [], customRoadTypes:  [],
  defaultWards:      [], customWards:      [],
  defaultMaterials:  [], customMaterials:  [],
};

const ClassificationContext = createContext({
  ...EMPTY_CLASSIFICATIONS,
  agencies: [], roadTypes: [], wards: [], materials: [],
  loading: false,
  requiresOrgSelection: false,
  fetchClassifications: () => Promise.resolve(),
  addClassification: () => Promise.resolve(),
  updateClassification: () => Promise.resolve(),
  deleteClassification: () => Promise.resolve(),
});

export const useClassification = () => useContext(ClassificationContext);

const VALID_CATEGORIES = new Set(['agencies', 'road_types', 'wards', 'materials']);

const getToken = () => {
  try {
    const raw = localStorage.getItem('token');
    return raw ? raw.replace(/^"|"$/g, '') : null;
  } catch { return null; }
};

// Backend returns flat snake_case keys; map them to the camelCase shape used
// throughout the React app in one place so callers never see the wire format.
const mapResult = (result = {}) => ({
  defaultAgencies:  result.default_agencies   || [],
  customAgencies:   result.custom_agencies    || [],
  defaultRoadTypes: result.default_road_types || [],
  customRoadTypes:  result.custom_road_types  || [],
  defaultWards:     result.default_wards      || [],
  customWards:      result.custom_wards       || [],
  defaultMaterials: result.default_materials  || [],
  customMaterials:  result.custom_materials   || [],
});

export const ClassificationProvider = ({ children }) => {
  const [state, setState] = useState(EMPTY_CLASSIFICATIONS);
  const [loading, setLoading] = useState(false);
  // True when the backend reports there is no single org to read (SuperAdmin in
  // the all-orgs state). The Settings tab shows an org-selection prompt instead.
  const [requiresOrgSelection, setRequiresOrgSelection] = useState(false);

  const fetchClassifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}classification/get`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setState(mapResult(res.data?.result));
      setRequiresOrgSelection(!!res.data?.requires_org_selection);
    } catch (err) {
      console.warn('Failed to fetch classifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addClassification = useCallback(async (category, name, short) => {
    if (!VALID_CATEGORIES.has(category)) {
      throw new Error(`Invalid category "${category}"`);
    }
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}classification/add`,
        { category, name, short },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setState(mapResult(res.data?.result));
    } catch (err) {
      console.error('Failed to add classification:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateClassification = useCallback(async (category, originalName, updates = {}) => {
    if (!VALID_CATEGORIES.has(category)) {
      throw new Error(`Invalid category "${category}"`);
    }
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BASE_URL}classification/update`,
        {
          category,
          original_name: originalName,
          new_name:  updates.name,
          new_short: updates.short,
          new_color: updates.color,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setState(mapResult(res.data?.result));
    } catch (err) {
      console.error('Failed to update classification:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteClassification = useCallback(async (category, name) => {
    if (!VALID_CATEGORIES.has(category)) {
      throw new Error(`Invalid category "${category}"`);
    }
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}classification/delete`,
        {
          data: { category, name },
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      setState(mapResult(res.data?.result));
    } catch (err) {
      console.error('Failed to delete classification:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Convenience: combined name lists for places that just want a dropdown.
  const namesOf = list => list.map(item => item.name);
  const agencies  = [...namesOf(state.defaultAgencies),  ...namesOf(state.customAgencies)];
  const roadTypes = [...namesOf(state.defaultRoadTypes), ...namesOf(state.customRoadTypes)];
  const wards     = [...namesOf(state.defaultWards),     ...namesOf(state.customWards)];
  const materials = [...namesOf(state.defaultMaterials), ...namesOf(state.customMaterials)];

  useEffect(() => {
    if (getToken()) fetchClassifications();
  }, [fetchClassifications]);

  return (
    <ClassificationContext.Provider value={{
      ...state,
      agencies, roadTypes, wards, materials,
      loading,
      requiresOrgSelection,
      fetchClassifications,
      addClassification,
      updateClassification,
      deleteClassification,
    }}>
      {children}
    </ClassificationContext.Provider>
  );
};

export default ClassificationContext;
